// ============================================================================
// BRANCH FORM MODAL — Add / Edit Branch (MSTR-BRN-001)
// ============================================================================
// Area dropdown labels include the parent region AND grandparent zone for
// context (full hierarchy clarity at the leaf level).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { branchService } from 'api/services/branchService';
import type { Branch } from 'api/types/branch.types';

type BranchStatus = 'ACTIVE' | 'INACTIVE';

interface BranchFormData {
  areaId: string;
  branchCode: string;
  branchName: string;
  statusCode: BranchStatus;
}

const INITIAL_FORM_DATA: BranchFormData = {
  areaId: '',
  branchCode: '',
  branchName: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface AreaOption {
  value: string;  // areaId
  label: string;  // e.g. "WB-KOL — Kolkata Area · West Bengal Region · East Zone"
}

export interface BranchFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: Branch | null;
  /** Active areas for the dropdown — passed from parent screen (already cascade-filtered). */
  areaOptions: AreaOption[];
  /** Seed options — used when editing a branch whose current parent area is
   * no longer in the active list (INACTIVE / DELETED / ancestor deactivated). */
  seedAreaOptions?: AreaOption[];
  onSaved: () => void;
  onClose: () => void;

  // Minimize support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const BranchFormModal: React.FC<BranchFormModalProps> = ({
  isOpen, mode, record, areaOptions, seedAreaOptions, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // Merge seed options into the active areas list so inactive/deleted parents render.
  const combinedAreaOptions = useMemo(() => {
    if (!seedAreaOptions || seedAreaOptions.length === 0) return areaOptions;
    const seedIds = new Set(seedAreaOptions.map(s => s.value));
    return [
      ...areaOptions.filter(o => !seedIds.has(o.value)),
      ...seedAreaOptions,
    ];
  }, [areaOptions, seedAreaOptions]);

  // Form state (Pattern B)
  const [formData, setFormData] = useState<BranchFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  // Minimize-state-capture refs
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  // Validation state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Populate / reset on open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: BranchFormData = rec
      ? {
          areaId: rec.areaId,
          branchCode: rec.branchCode,
          branchName: rec.branchName,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as BranchStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  // Minimize handler
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.branchCode || currentFormData.branchCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.branchCode?.trim();
      const name = currentFormData.branchName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.branchCode || currentRecord?.branchCode || 'Branch'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.branchCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // Validate and open save confirmation
  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.branchCode.trim();
    const name = formData.branchName.trim();

    if (!formData.areaId) errors.areaId = 'Area is required';

    if (!code) errors.branchCode = 'Branch code is required';
    else if (code.length > 20) errors.branchCode = 'Max 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.branchCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.branchName = 'Branch name is required';
    else if (name.length > 100) errors.branchName = 'Max 100 characters';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // Confirmed save
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.branchCode.trim().toUpperCase();
      const name = formData.branchName.trim();

      if (mode === 'create' || mode === 'copy') {
        await branchService.create({
          areaId: formData.areaId,
          branchCode: code,
          branchName: name,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Branch "${code}" created successfully.`);
      } else if (record) {
        await branchService.update(record.id, {
          areaId: formData.areaId,
          branchCode: code,
          branchName: name,
          statusCode: formData.statusCode,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Branch "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This branch was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A branch with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save branch. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'view' ? 'View Branch' : mode === 'copy' ? 'Copy Branch' : mode === 'create' ? 'Add Branch' : 'Edit Branch'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          {/* Area (parent FK). Labels include parent region + grand-parent zone
              so the user sees the full hierarchy before choosing. */}
          <div className="form-group" data-field="areaId">
            <SyutSelect
              label={<>Area <span className="text-danger">*</span></>}
              required
              options={combinedAreaOptions}
              value={formData.areaId}
              onChange={(val) => {
                clearFieldError('areaId');
                setFormData(prev => ({ ...prev, areaId: (val as string) || '' }));
              }}
              error={getFieldError('areaId')}
              isClearable={false}
              placeholder="Select an area..."
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>

          {/* Branch Code */}
          <div className="form-group" data-field="branchCode">
            <label className="form-label">
              Branch Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('branchCode') ? ' form-field-error' : ''}`}
              value={formData.branchCode}
              maxLength={20}
              placeholder="DEL-C-001, KA-BLR-014..."
              onChange={(e) => {
                clearFieldError('branchCode');
                setFormData(prev => ({ ...prev, branchCode: e.target.value.toUpperCase() }));
              }}
              disabled={isSaving}
            />
            {getFieldError('branchCode') && (
              <div className="form-error-message">{getFieldError('branchCode')}</div>
            )}
          </div>

          {/* Branch Name */}
          <div className="form-group" data-field="branchName">
            <label className="form-label">
              Branch Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('branchName') ? ' form-field-error' : ''}`}
              value={formData.branchName}
              maxLength={100}
              placeholder="Connaught Place Branch"
              onChange={(e) => {
                clearFieldError('branchName');
                setFormData(prev => ({ ...prev, branchName: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('branchName') && (
              <div className="form-error-message">{getFieldError('branchName')}</div>
            )}
          </div>

          {/* Status */}
          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(val) => setFormData(prev => ({
                ...prev,
                statusCode: ((val as string) || 'ACTIVE') as BranchStatus,
              }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          (mode === 'create' || mode === 'copy')
            ? `Create branch "${formData.branchCode.trim().toUpperCase()}"?`
            : `Update branch "${formData.branchCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default BranchFormModal;
